/**
 * Shared utility functions for the Clinic Admin Portal.
 *
 * Previously duplicated across ClinicAdminLayout, DashboardPage,
 * ManageFamilyDoctorsPage, NewPatientRequestsPage, and ClinicProfilePage.
 */

/** Trim and return string, or null if empty/undefined/null. */
export function asString(v: any): string | null {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s.length ? s : null;
}

/** Parse to finite number, or 0 if invalid. */
export function asNumber(v: any): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Return first non-null/undefined value from obj[key] for the given keys. */
export function pickFirst<T = any>(obj: any, keys: string[]): T | undefined {
  if (!obj) return undefined;
  for (const k of keys) {
    const val = obj[k];
    if (val !== undefined && val !== null) return val as T;
  }
  return undefined;
}

/** Return first non-empty trimmed string from obj[key] for the given keys. */
export function pickFirstString(obj: any, keys: string[]): string | null {
  if (!obj) return null;
  for (const k of keys) {
    const val = asString(obj[k]);
    if (val) return val;
  }
  return null;
}

/** Format boolean as "Yes" / "No" / "—". */
export function formatYesNo(v: boolean | null | undefined): string {
  if (v === true) return "Yes";
  if (v === false) return "No";
  return "—";
}

/** Format ISO date string to locale date, or "—". */
export function formatDateMaybe(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

/** Format ISO date string to locale date+time, or "—". */
export function formatDateTime(val: string | null | undefined): string {
  if (!val) return "—";
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return val;
    return d.toLocaleString();
  } catch {
    return val;
  }
}

/**
 * Format a date-only or ISO date string to locale date only, or "—".
 *
 * Important:
 * - If val is "YYYY-MM-DD" (date-only), do NOT use `new Date(val)` because
 *   JS treats it as UTC midnight and localizes it, causing off-by-one in ET.
 * - For true timestamps, keep existing behavior.
 */
export function formatDateOnly(val: string | null | undefined): string {
  if (!val) return "—";

  try {
    const s = String(val).trim();
    if (!s) return "—";

    // ✅ Date-only: "YYYY-MM-DD" => render as a pure calendar date (no timezone)
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m) {
      const yyyy = Number(m[1]);
      const mm = Number(m[2]);
      const dd = Number(m[3]);

      if (Number.isFinite(yyyy) && Number.isFinite(mm) && Number.isFinite(dd)) {
        // Use Date.UTC to avoid local timezone shifting, then format in UTC.
        const utc = new Date(Date.UTC(yyyy, mm - 1, dd));
        return new Intl.DateTimeFormat(undefined, { timeZone: "UTC" }).format(
          utc
        );
      }

      // If parsing fails for any reason, fall back to raw string.
      return s;
    }

    // Default: treat as timestamp and localize (existing behavior)
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString();
  } catch {
    return val;
  }
}